import * as Yup from 'yup'
import {
  PaymentType,
  PlanTypeGCP,
  GcpPostPaymentTypes,
} from '../../modules/privateOffer/reducer'

import _, { isNil } from 'lodash'
import { gcpBillingIdRegex } from './constants'

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = _.get(item, propertyPath)
      if (
        propertyValue &&
        _.filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!_.isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

const getTotalMonths = (paymentType: PaymentType, serviceLength: number) => {
  if (paymentType === PaymentType.MONTHLY) {
    return serviceLength
  } else if (paymentType === PaymentType.QUARTERLY) {
    return 3 * serviceLength
  } else if (paymentType === PaymentType.ANNUALLY) {
    return 12 * serviceLength
  }
}

const metricPricing = Yup.array().when(['individualDiscount'], {
  is: (individualDiscount: boolean) => {
    return individualDiscount
  },
  then: Yup.array().of(
    Yup.object().shape({
      discount: Yup.number()
        .typeError('Please enter a number')
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        })
        .required('Please enter a number'),
    })
  ),
})

const privateOfferAddEditValidationSchema = Yup.object().shape({
  //add validation here
  companyName: Yup.string().required('Company name cannot be empty.'),
  buyerCloudBillingId: Yup.string()
    .required('GCP billing account ID cannot be empty.')
    .matches(gcpBillingIdRegex, 'Please enter correct billing account ID.'),
  emailCustomMessage: Yup.string().required(
    'Please enter the Custom email message.'
  ),
  offerRecipients: Yup.array()
    .of(
      Yup.object().shape({
        firstName: Yup.string().required('First Name cannot be empty.'),
        lastName: Yup.string().required('Last Name cannot be empty.'),
        title: Yup.string(),
        email: Yup.string()
          .email('Enter valid email.')
          .required('Email cannot be empty.'),
      })
    )
    .uniqueProperty('email', 'Duplicate emails is not allowed.'),

  offerStakeholders: Yup.array()
    .of(
      Yup.object().shape({
        firstName: Yup.string().required('First Name cannot be empty.'),
        lastName: Yup.string().required('Last Name cannot be empty.'),
        email: Yup.string()
          .email('Enter valid email.')
          .required('Email cannot be empty.'),
      })
    )
    .uniqueProperty('email', 'Duplicate emails is not allowed.'),

  notes: Yup.string(),
  privateOfferName: Yup.string().required('Offer name cannot be empty.'),
  offerExpirationDate: Yup.string()
    .required('Date field cannot be empty.')
    .nullable(),
  autoRenew: Yup.boolean(),
  renewalCount: Yup.number().when(
    ['plan.paymentType', 'autoRenew', 'plan.planType'],
    {
      is: (
        paymentType: PaymentType,
        autoRenew: boolean,
        planType: PlanTypeGCP
      ) => paymentType && autoRenew && planType !== PlanTypeGCP.USAGE,
      then: (schema: any) => {
        return schema
          .typeError('Please enter a number.')
          .required('Please enter a number.')
          .positive('Please enter a positive number')
          .min(1, 'Number of renewals should be greater than or equal to 1')
          .test(
            'renewalCount',
            (value?: number, context?: Yup.TestContext<any>) => {
              const planPaymentType: PaymentType =
                context?.parent?.plan?.paymentType
              const planServiceLength: number =
                context?.parent?.plan?.serviceLength
              if (!value) return false
              if (value <= 0) return false
              if (!planPaymentType || !planServiceLength) return true
              const totalMonths = getTotalMonths(
                planPaymentType,
                planServiceLength
              )
              if ((totalMonths as number) <= 12) {
                if (value > 4) {
                  return context?.createError({
                    path: context?.path,
                    message: 'Number of renewals can be between 1 to 4.',
                  })
                }
              } else if ((totalMonths as number) <= 24) {
                if (value > 2) {
                  return context?.createError({
                    path: context?.path,
                    message: 'Number of renewals can be 1 or 2.',
                  })
                }
              } else {
                if (value > 1) {
                  return context?.createError({
                    path: context?.path,
                    message: 'Number of renewals can only be 1.',
                  })
                }
              }
              return true
            }
          )
      },
    }
  ),

  plan: Yup.object().shape({
    planType: Yup.string().required(),
    paymentType: Yup.string(),
    planId: Yup.string().required(),
    serviceLength: Yup.number()
      .typeError('Please enter a number.')
      .required('Please enter a number.')
      .test('service-length-with-payment-type', function (value) {
        if (isNil(value)) {
          return true
        }
        const paymentType = this.parent.paymentType
        const planType = this.parent.planType
        if (
          paymentType === PaymentType.MONTHLY ||
          planType === PlanTypeGCP.USAGE
        ) {
          if (value < 1) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at least 1 month for Monthly.',
            })
          }
          if (value > 60) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at most 60 months for Monthly.',
            })
          }
        }
        if (paymentType === PaymentType.QUARTERLY) {
          if (value < 1) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at least 1 quarter for Quarterly.',
            })
          }
          if (value > 20) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at most 20 quarters for Quarterly.',
            })
          }
        }
        if (paymentType === PaymentType.ANNUALLY) {
          if (value < 1) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at least 1 year for Annually.',
            })
          }
          if (value > 5) {
            return this.createError({
              path: this.path,
              message:
                'Contract Duration must be at most 5 years for Annually.',
            })
          }
        }
        return true
      }),
    flatFeePrice: Yup.number()
      .typeError('Please enter a number')
      .test('flat-fee-with-payment-type', function (value) {
        const paymentType = this.parent.paymentType
        if (paymentType === PaymentType.MONTHLY) {
          if (isNil(value)) {
            return this.createError({
              path: this.path,
              message: 'Please enter monthly flat fee.',
            })
          }
        }
        if (paymentType === PaymentType.QUARTERLY) {
          if (isNil(value)) {
            return this.createError({
              path: this.path,
              message: 'Please enter quarterly flat fee.',
            })
          }
        }
        if (paymentType === PaymentType.ANNUALLY) {
          if (isNil(value)) {
            return this.createError({
              path: this.path,
              message: 'Please enter annually flat fee.',
            })
          }
        }
        return true
      })
      .checkDecimalpointValidation({
        message: 'Only 2 decimal places allowed.',
      }),
    tcv: Yup.number().when(['planType', 'paymentType'], {
      is: (planType: PlanTypeGCP, paymentType?: PaymentType) =>
        (planType === PlanTypeGCP.FLAT_FEE ||
          planType === PlanTypeGCP.FLAT_FEE_USAGE) &&
        paymentType === PaymentType.CUSTOM,
      then: Yup.number()
        .typeError('Please enter a number')
        .required('Please enter a total contract value'),
      otherwise: Yup.number().nullable(),
    }),
    installmentSchedule: Yup.string().when(['planType', 'paymentType'], {
      is: (planType: PlanTypeGCP, paymentType?: PaymentType) =>
        (planType === PlanTypeGCP.FLAT_FEE ||
          planType === PlanTypeGCP.FLAT_FEE_USAGE) &&
        paymentType === PaymentType.CUSTOM,
      then: Yup.string().required('Please select installment schedule'),
      otherwise: Yup.string().nullable(),
    }),
    individualDiscount: Yup.bool(),
    discount: Yup.number().when(['planType', 'individualDiscount'], {
      is: (planType: PlanTypeGCP, individualDiscount?: boolean) =>
        (planType === PlanTypeGCP.USAGE ||
          planType === PlanTypeGCP.FLAT_FEE_USAGE) &&
        !individualDiscount,
      then: Yup.number()
        .typeError('Please enter a number')
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        })
        .required('Please enter percentage of discount on usage'),
      otherwise: Yup.number(),
    }),
    metricPricing: Yup.array().when(
      ['planType', 'individualDiscount', 'paymentType'],
      {
        is: (
          planType: PlanTypeGCP,
          individualDiscount?: boolean,
          paymentType?: PaymentType
        ) => {
          return (
            planType === PlanTypeGCP.USAGE ||
            (planType === PlanTypeGCP.FLAT_FEE_USAGE &&
              paymentType &&
              GcpPostPaymentTypes.includes(paymentType) &&
              individualDiscount)
          )
        },
        then: metricPricing,
        otherwise: Yup.array(),
      }
    ),
    installmentInfo: Yup.array().when(
      ['planType', 'paymentType', 'individualDiscount'],
      {
        is: (
          planType: PlanTypeGCP,
          paymentType?: PaymentType,
          individualDiscount?: boolean
        ) =>
          (planType === PlanTypeGCP.FLAT_FEE ||
            planType === PlanTypeGCP.FLAT_FEE_USAGE) &&
          paymentType === PaymentType.CUSTOM &&
          individualDiscount,
        then: Yup.array().of(
          Yup.object().shape({
            metricPricing: metricPricing,
          })
        ),
        otherwise: Yup.array(),
      }
    ),
  }),

  eulaType: Yup.string(),
  eulaFile: Yup.array(),
})
export default privateOfferAddEditValidationSchema
